import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, User, Loader2, Tag } from "lucide-react";
import Reveal from "../components/Reveal";
import api, { mediaUrl } from "../lib/api";
import useSeo from "../hooks/useSeo";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await api.get(`/blog/posts/${slug}`);
        setPost(res.data);
        const list = await api.get("/blog/posts");
        setOthers(list.data.filter((p) => p.slug !== slug).slice(0, 3));
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  useSeo({
    title: post ? `${post.metaTitle || post.title} \u2014 Elite Vending` : "Elite Vending Blog",
    description: post ? (post.metaDescription || post.excerpt) : "",
    image: post ? mediaUrl(post.image) : "",
    url: typeof window !== "undefined" ? window.location.href : "",
  });

  if (loading) {
    return (
      <main className="min-h-[70vh] grid place-items-center">
        <Loader2 className="animate-spin text-gold-dark" size={40} />
      </main>
    );
  }

  if (notFound || !post) {
    return (
      <main className="pt-32 pb-24 min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-3xl text-ink">Article not found</h1>
          <p className="mt-3 text-muted-foreground">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 h-11 font-semibold text-ink hover:bg-gold-dark transition-colors">
            <ArrowLeft size={16} /> Back to Blog
          </Link>
        </div>
      </main>
    );
  }

  const blocks = post.blocks && post.blocks.length ? post.blocks : [{ type: "p", text: post.excerpt }];

  return (
    <main>
      {/* Hero */}
      <section className="relative bg-ink pt-32 pb-14 overflow-hidden">
        <div className="pointer-events-none absolute -top-16 right-10 h-72 w-72 rounded-full bg-gold/15 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-gold transition-colors">
            <ArrowLeft size={15} /> All articles
          </Link>
          <div className="mt-5 flex items-center gap-3 text-xs">
            <span className="rounded-full bg-gold px-3 py-1 font-semibold uppercase tracking-widest text-ink">{post.category}</span>
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">{post.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-white/60">
            <span className="flex items-center gap-1.5"><User size={14} className="text-gold" /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} className="text-gold" /> {post.date}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} className="text-gold" /> {post.readTime}</span>
          </div>
        </div>
      </section>

      {/* Cover image */}
      {post.image && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-gold/20">
            <img src={mediaUrl(post.image)} alt={post.heroImageAlt || post.title} className="w-full h-64 sm:h-96 object-cover" />
          </div>
        </div>
      )}

      {/* Body */}
      <article className="bg-white py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.excerpt && <p className="text-xl text-ink font-medium leading-relaxed">{post.excerpt}</p>}
          <div className="mt-8 space-y-6">
            {blocks.map((block, i) => {
              if (block.type === "h") {
                return <h2 key={i} className="font-display text-2xl text-ink pt-2">{block.text}</h2>;
              }
              if (block.type === "quote") {
                return (
                  <blockquote key={i} className="border-l-4 border-gold bg-gold-soft/50 rounded-r-xl px-6 py-5 my-2">
                    <p className="font-display text-lg text-ink italic">“{block.text}”</p>
                  </blockquote>
                );
              }
              return <p key={i} className="text-[17px] text-muted-foreground leading-[1.8]">{block.text}</p>;
            })}
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-10 flex flex-wrap items-center gap-2">
              <Tag size={15} className="text-gold-dark" />
              {post.tags.map((t) => (
                <span key={t} className="rounded-full bg-gold-soft/60 px-3 py-1 text-xs font-medium text-ink">#{t}</span>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 rounded-2xl bg-ink p-8 text-center">
            <h3 className="font-display text-2xl text-white">Ready to see it in your workplace?</h3>
            <p className="mt-2 text-white/60">Book a free site visit and we'll confirm eligibility in minutes.</p>
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-7 h-12 font-semibold text-ink hover:bg-gold-light transition-colors"
            >
              Book Free Site Visit <ArrowUpRight size={17} />
            </Link>
          </div>
        </div>
      </article>

      {/* Related */}
      {others.length > 0 && (
        <section className="bg-gold-soft/40 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl text-ink">Keep reading</h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {others.map((p, i) => (
                <Reveal key={p.slug} delay={i * 70}>
                  <Link to={`/blog/${p.slug}`} className="group block h-full rounded-2xl border border-gold/20 bg-white overflow-hidden hover:shadow-xl hover:border-gold transition-all duration-300">
                    <div className="relative h-44 overflow-hidden">
                      <img src={mediaUrl(p.image)} alt={p.heroImageAlt || p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <span className="absolute top-3 left-3 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-ink">{p.category}</span>
                    </div>
                    <div className="p-5">
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Calendar size={13} /> {p.date}</span>
                      <h3 className="mt-2 font-display text-lg text-ink leading-snug">{p.title}</h3>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-dark group-hover:gap-2.5 transition-all">
                        Read more <ArrowUpRight size={15} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
